import './index.css'

import styles from './index.module.css'

import React from 'react'

import mixpanel from '@disco/api/mixpanel'
import { sendDiscoverMixpanelEvent } from '@disco/utils/mixpanel/discover-page'

import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'

import Carousel from 'components/common/carousel'
import LibraryGridItem from 'components/common/library-grid-item'
import { Box, Flex, Heading } from 'components/ds'
import PageContainer from 'components/ui/page-container'
import Spinner from 'components/ui/spinner'

import SeeAllLink from '../components/see-all-link'
import { getCatalogBusinessNameFromUrl } from '../utils'
import CatalogAndTrackSection from './catalog-tracks'

/**
 * Groups items into slides for the carousel stacked layout.
 * Example: groupIntoSlides([a, b, c, d, e], 2) => [[a, b], [c, d], [e]]
 * Each group becomes one carousel slide with items stacked vertically.
 */
const groupIntoSlides = <T,>(items: T[], itemsPerSlide: number): T[][] => {
  const slides: T[][] = []
  for (let i = 0; i < items.length; i += itemsPerSlide) {
    slides.push(items.slice(i, i + itemsPerSlide))
  }
  return slides
}

type Categories = {
  client_libraries: (ClientLibraryDiscoveryPanelItem | null | undefined)[]
  name: string
  id: string
}[]
type Props = {
  categories: Categories
  loading: boolean
  data: {
    easyAccess: boolean
    query?: string
  }
}

const CategoryLibraries = ({
  categories,
  loading,
  data: { easyAccess, query },
}: Props): React.ReactNode | Array<React.ReactNode> => {
  if (loading) return <Spinner />
  return categories.map<React.ReactNode>(category => {
    const listGridLibrary = category.client_libraries
      .filter(lib => {
        if (easyAccess && !query) {
          if (lib) {
            return Boolean(lib.status && lib.status === 'approved')
          }
        } else {
          return Boolean(lib)
        }
      })
      .map<React.ReactNode>(lib => {
        if (!lib) return null
        const featured = category.name.toLowerCase().includes('featured')
        const businessName = getCatalogBusinessNameFromUrl(
          lib?.public_url || '',
        )

        if (featured) {
          return (
            <Flex key={lib.id} direction="row" gap="5">
              <CatalogAndTrackSection
                catalog={lib}
                isFeatured={featured}
                businessName={businessName}
              />
            </Flex>
          )
        }
        return (
          <LibraryGridItem
            key={lib.id}
            lib={lib}
            onClick={() => {
              sendDiscoverMixpanelEvent('Click Image', {
                context: 'Discover Music Home',
                catalogId: lib.id,
                catalogBusiness: businessName,
              })

              mixpanel.sendPureEvent(
                `Open${
                  featured ? ' featured' : ''
                } Catalog from Browse Catalogs`,
                {
                  'Library name': lib.display_name,
                  Category: category?.name,
                  Catalog: lib.id,
                },
              )
            }}
            type="shadow"
          />
        )
      })
    if (listGridLibrary.length === 0) return null

    const isFeatured = category.name.toLowerCase().includes('featured')
    const hasSmallCount = listGridLibrary.length <= 2
    // Use single row layout for featured catalogs or when there are 2 or fewer items
    const useSingleRow = isFeatured || hasSmallCount

    // Featured catalogs / small sections: 1 card per slide
    // Other catalogs: 2 cards per slide (stacked vertically)
    const carouselContent = useSingleRow
      ? listGridLibrary
      : groupIntoSlides(listGridLibrary, 2).map((group, index) => (
          <div key={index} className={styles.slideGrid}>
            {group}
          </div>
        ))

    // Use filter=is_favorite for favorited catalogs, group filter for others
    const seeAllUrl =
      category.id === 'favorited'
        ? '/discover/search?filter=is_favorite'
        : `/discover/search?group=${encodeURIComponent(category.name)}`

    const seeAllAction = <SeeAllLink to={seeAllUrl} />

    return (
      <Box className="BrowseLibraries_Section" key={category.id}>
        <PageContainer.Section
          title={
            <Heading as="h2" size="5">
              {category.name}
            </Heading>
          }
          action={seeAllAction}
        >
          <div className={styles.carouselWrapper}>
            <Carousel
              navKey={`category-${category.id}`}
              slidesPerView="auto"
              spaceBetween={16}
              useContainerBreakpoints
            >
              {carouselContent}
            </Carousel>
          </div>
        </PageContainer.Section>
      </Box>
    )
  })
}

export default CategoryLibraries
